import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowUp, Loader2 } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { getAttribution } from "@/lib/attribution";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Must match ALLOWED_BUSINESS_TYPES in /app/backend/leads.py
const BUSINESS_TYPES = [
  "Plumbing",
  "HVAC",
  "Roofing",
  "Electrical",
  "Landscaping",
  "Cleaning",
  "Construction",
  "Automotive Repair",
  "Truck / Diesel Repair",
  "Trucking / Transportation",
  "CDL School",
  "Restaurant / Food",
  "Salon / Beauty",
  "Professional Services",
  "Retail",
  "Other",
];

const DOMAIN_OPTIONS = [
  "I already own a domain",
  "I need a domain",
  "I'm not sure",
];

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRe = /^[+()0-9\s.-]{7,}$/;

export const LeadForm = () => {
  const [values, setValues] = useState({
    fullName: "",
    businessName: "",
    email: "",
    phone: "",
    businessType: "",
    cityState: "",
    description: "",
    currentWebsite: "",
    domainStatus: "",
    company_website_hp: "", // honeypot
  });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) =>
    setValues((v) => ({ ...v, [key]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!values.fullName.trim()) e.fullName = "Please enter your full name.";
    if (!values.businessName.trim())
      e.businessName = "Please enter your business name.";
    if (!emailRe.test(values.email.trim()))
      e.email = "Please enter a valid email address.";
    if (!phoneRe.test(values.phone.trim()))
      e.phone = "Please enter a valid phone number.";
    if (!values.businessType) e.businessType = "Please select a business type.";
    if (!values.cityState.trim())
      e.cityState = "Please enter your city and state.";
    if (values.description.trim().length < 10)
      e.description = "Tell us a little more about your business.";
    return e;
  };

  // Map backend field paths back to our camelCase keys for inline error display.
  const BACKEND_FIELD_MAP = {
    full_name: "fullName",
    business_name: "businessName",
    email: "email",
    phone: "phone",
    business_type: "businessType",
    city_state: "cityState",
    business_description: "description",
    current_website: "currentWebsite",
    domain_status: "domainStatus",
  };

  const buildPayload = () => ({
    full_name: values.fullName.trim(),
    business_name: values.businessName.trim(),
    email: values.email.trim(),
    phone: values.phone.trim(),
    business_type: values.businessType,
    city_state: values.cityState.trim(),
    business_description: values.description.trim(),
    current_website: values.currentWebsite.trim() || null,
    domain_status: values.domainStatus || null,
    company_website_hp: values.company_website_hp || "",
    ...getAttribution(),
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setFormError("");

    // Client-side quick check first (backend re-validates authoritatively)
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      const firstKey = Object.keys(nextErrors)[0];
      const el = document.querySelector(`[name="${firstKey}"]`);
      if (el && el.focus) el.focus();
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });

      if (res.ok) {
        setSubmitted(true);
        return;
      }

      if (res.status === 429) {
        setFormError(
          "You've submitted several requests recently. Please try again in a few minutes.",
        );
        return;
      }

      if (res.status === 400) {
        // { ok:false, message, fields: { field: msg } }
        let body = null;
        try {
          body = await res.json();
        } catch {
          /* ignore */
        }
        const inline = {};
        if (body && body.fields) {
          Object.entries(body.fields).forEach(([backendKey, msg]) => {
            const local = BACKEND_FIELD_MAP[backendKey];
            if (local) inline[local] = msg;
          });
        }
        setErrors(inline);
        setFormError(
          (body && body.message) ||
            "Please check the highlighted fields and try again.",
        );
        return;
      }

      // 5xx or other unexpected
      setFormError(
        "Something went wrong on our side. Please try again in a moment.",
      );
    } catch (err) {
      // Network / connectivity failure
      setFormError(
        "Something went wrong on our side. Please try again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const scrollTop = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section
      id="lead-form"
      className="bg-goc-light"
      data-testid="section-lead-form"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-24">
        {submitted ? (
          <div
            className="bg-white border border-goc-border rounded-2xl shadow-sm p-8 md:p-12 text-center"
            role="status"
            aria-live="polite"
            data-testid="lead-form-success"
          >
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-goc-primary/10 text-goc-primary">
              <CheckCircle2 className="h-7 w-7" strokeWidth={2.25} />
            </span>
            <h2 className="mt-5 text-2xl md:text-3xl font-bold text-goc-text">
              Thanks! We Got It.
            </h2>
            <p className="mt-3 text-goc-muted leading-relaxed max-w-lg mx-auto">
              Your website request has been received. We&apos;ll review your
              information and contact you soon.
            </p>
            <button
              type="button"
              onClick={scrollTop}
              className="mt-8 inline-flex items-center gap-2 rounded-lg border border-goc-border bg-white px-5 py-3 text-sm font-semibold text-goc-text hover:border-goc-primary hover:text-goc-primary transition-colors"
              data-testid="lead-form-back-to-top"
            >
              <ArrowUp className="h-4 w-4" />
              Back to Top
            </button>
          </div>
        ) : (
          <>
            <SectionHeading
              eyebrow="Get Started"
              title="Get Your Website Started"
              subtitle="Tell us about your business and we'll get in touch to discuss your website."
              testId="heading-lead-form"
            />

            <form
              className="bg-white border border-goc-border rounded-2xl shadow-sm p-6 md:p-8 space-y-5"
              onSubmit={onSubmit}
              noValidate
              data-testid="lead-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field
                  label="Full Name"
                  name="fullName"
                  value={values.fullName}
                  onChange={update("fullName")}
                  error={errors.fullName}
                  required
                />
                <Field
                  label="Business Name"
                  name="businessName"
                  value={values.businessName}
                  onChange={update("businessName")}
                  error={errors.businessName}
                  required
                />
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={values.email}
                  onChange={update("email")}
                  error={errors.email}
                  required
                />
                <Field
                  label="Phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  value={values.phone}
                  onChange={update("phone")}
                  error={errors.phone}
                  required
                />
                <SelectField
                  label="Business Type"
                  name="businessType"
                  value={values.businessType}
                  onChange={update("businessType")}
                  error={errors.businessType}
                  options={BUSINESS_TYPES}
                  placeholder="Select a business type"
                  required
                />
                <Field
                  label="City / State"
                  name="cityState"
                  value={values.cityState}
                  onChange={update("cityState")}
                  error={errors.cityState}
                  placeholder="Chicago, IL"
                  required
                />
              </div>

              <TextAreaField
                label="Tell Us About Your Business"
                name="description"
                value={values.description}
                onChange={update("description")}
                error={errors.description}
                placeholder="Services, service area, what you need in a website, etc."
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field
                  label="Current Website (optional)"
                  name="currentWebsite"
                  value={values.currentWebsite}
                  onChange={update("currentWebsite")}
                  error={errors.currentWebsite}
                  placeholder="https://"
                />
                <SelectField
                  label="Domain Status (optional)"
                  name="domainStatus"
                  value={values.domainStatus}
                  onChange={update("domainStatus")}
                  error={errors.domainStatus}
                  options={DOMAIN_OPTIONS}
                  placeholder="Select an option"
                />
              </div>

              {/*
               * Honeypot: bots that auto-fill likely-labeled inputs will
               * populate "Company Website"; a legitimate user will never see
               * or reach this field. Keep off-screen (not display:none — some
               * bots skip display:none), non-focusable, and aria-hidden.
               */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  top: "auto",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              >
                <label htmlFor="company_website_hp">Company Website</label>
                <input
                  id="company_website_hp"
                  name="company_website_hp"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={values.company_website_hp}
                  onChange={update("company_website_hp")}
                />
              </div>

              <div>
                {formError ? (
                  <div
                    role="alert"
                    className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                    data-testid="lead-form-error"
                  >
                    {formError}
                  </div>
                ) : null}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-goc-primary px-6 py-3.5 text-base font-semibold text-white hover:bg-goc-deep transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm shadow-goc-primary/30"
                  data-testid="lead-form-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      Sending…
                    </>
                  ) : (
                    "Get My Website"
                  )}
                </button>
                <p className="mt-3 text-xs text-goc-muted leading-relaxed">
                  No payment required to request information. We&apos;ll contact you
                  before any work begins.{" "}
                  <Link
                    to="/privacy"
                    className="underline hover:text-goc-primary transition-colors"
                    data-testid="lead-form-privacy-link"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </form>
          </>
        )}
      </div>
    </section>
  );
};

const inputCls =
  "w-full bg-white border border-goc-border rounded-md px-4 py-2.5 text-goc-text placeholder:text-goc-muted/70 focus:ring-2 focus:ring-goc-primary/40 focus:border-goc-primary outline-none transition-colors";

const labelCls = "block text-sm font-medium text-goc-text mb-1.5";

const Field = ({ label, name, error, required, ...rest }) => (
  <label className="block" htmlFor={name}>
    <span className={labelCls}>
      {label}
      {required ? <span className="text-goc-primary">*</span> : null}
    </span>
    <input
      id={name}
      name={name}
      className={inputCls}
      aria-invalid={!!error}
      aria-describedby={error ? `${name}-error` : undefined}
      data-testid={`lead-input-${name}`}
      {...rest}
    />
    {error ? (
      <p
        id={`${name}-error`}
        className="mt-1.5 text-xs text-red-600"
        data-testid={`lead-error-${name}`}
      >
        {error}
      </p>
    ) : null}
  </label>
);

const SelectField = ({
  label,
  name,
  error,
  required,
  options,
  placeholder,
  ...rest
}) => (
  <label className="block" htmlFor={name}>
    <span className={labelCls}>
      {label}
      {required ? <span className="text-goc-primary">*</span> : null}
    </span>
    <select
      id={name}
      name={name}
      className={inputCls}
      aria-invalid={!!error}
      aria-describedby={error ? `${name}-error` : undefined}
      data-testid={`lead-select-${name}`}
      {...rest}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
    {error ? (
      <p
        id={`${name}-error`}
        className="mt-1.5 text-xs text-red-600"
        data-testid={`lead-error-${name}`}
      >
        {error}
      </p>
    ) : null}
  </label>
);

const TextAreaField = ({ label, name, error, required, ...rest }) => (
  <label className="block" htmlFor={name}>
    <span className={labelCls}>
      {label}
      {required ? <span className="text-goc-primary">*</span> : null}
    </span>
    <textarea
      id={name}
      name={name}
      rows={5}
      className={inputCls}
      aria-invalid={!!error}
      aria-describedby={error ? `${name}-error` : undefined}
      data-testid={`lead-textarea-${name}`}
      {...rest}
    />
    {error ? (
      <p
        id={`${name}-error`}
        className="mt-1.5 text-xs text-red-600"
        data-testid={`lead-error-${name}`}
      >
        {error}
      </p>
    ) : null}
  </label>
);

export default LeadForm;
